import { Module } from 'vuex'
import axios from 'axios'
import { AuthState, RootState } from '@/store/types'
import { MutationTypes } from '@/store/mutation-types'
import { apolloClient, onLogin, onLogout } from '@/plugins/vue-apollo'
import RegisterMutation from '@/graphql/user/register.gql'
import LoginMutation from '@/graphql/user/login.gql'
import LogoutMutation from '@/graphql/user/logout.gql'
import ResendVerificationMutation from '@/graphql/user/resendVerificationEmail.gql'
import ForgotPasswordMutation from '@/graphql/user/forgotPassword.gql'
import ResetPasswordMutation from '@/graphql/user/resetPassword.gql'
import UpdatePasswordMutation from '@/graphql/user/updatePassword.gql'
import UpdateProfileMutation from '@/graphql/user/updateProfile.gql'
import {
  ForgotPasswordInput,
  MemberType,
  ResetPasswordInput,
  UpdatePasswordInput,
  UpdateProfileInput,
  User,
} from '@/types/generated/graphql'

const NoUser: User = {
  userId: '',
  email: '',
  emailVerified: false,
  createdAt: '',
  profile: {
    id: '',
    userId: '',
    firstName: '',
    lastName: '',
    firstNameKana: '',
    lastNameKana: '',
    name: {
      first: '',
      last: '',
      fullName: '',
    },
    nameKana: {
      first: '',
      last: '',
      fullName: '',
    },
  },
  members: [],
}

export const auth: Module<Partial<AuthState>, RootState> = {
  namespaced: true,
  state: () => ({
    user: NoUser,
  }),
  getters: {
    /**
     * ログイン済であればtrueを返します。
     * @param state
     */
    isAuthenticated: (state) => {
      return state.user && state.user.userId !== ''
    },
    /**
     * メール検証済みであればtrueを返します。
     * @param state
     */
    isEmailVerified: (state) => state.user?.emailVerified,
    /**
     * 現在のユーザが、指定されたメンバータイプ権限を持っている場合はtrueを返します。
     * @param state
     */
    // TODO : 現在、ユーザは１パートナーのみしか所属できないため、１パートナー固定の前提で実装。
    // 複数パートナーに所属できる場合は、UIを含めて検討
    authorizedTo: (state) => (memberType: MemberType) => {
      const member = state.user?.members?.find(() => true)
      if (!member) {
        return false
      }
      if (member.type === MemberType.Owner) {
        return true
      }
      if (member.type === MemberType.StoreOwner) {
        return memberType !== MemberType.Owner
      }
      return memberType === MemberType.Individual
    },
    partnerId: (state) => {
      return state.user?.members?.find((nonNull) => nonNull)?.partnerId
    },
  },
  mutations: {
    [MutationTypes.AUTH_REGISTER](state, payload: AuthState) {
      state.user = {
        ...payload.user,
      }
    },
    [MutationTypes.AUTH_LOGIN](state, payload: AuthState) {
      state.user = {
        ...payload.user,
      }
    },
    [MutationTypes.AUTH_LOGOUT](state) {
      state.user = NoUser
    },
    [MutationTypes.AUTH_UPDATE_PROFILE](state, payload: AuthState) {
      if (!state.user) {
        return
      }
      state.user.profile = payload.user.profile
    },
  },
  actions: {
    register: async ({ commit }, user: AuthState['user']) => {
      await axios.get('/sanctum/csrf-cookie')
      const { data } = await apolloClient.mutate({
        mutation: RegisterMutation,
        variables: { input: user },
      })
      commit(MutationTypes.AUTH_REGISTER, { user: data.register.user })
      await onLogin(apolloClient)
    },
    login: async ({ commit }, user: AuthState['user']) => {
      await axios.get('/sanctum/csrf-cookie')
      const { data } = await apolloClient.mutate({
        mutation: LoginMutation,
        variables: { input: user },
      })
      commit(MutationTypes.AUTH_LOGIN, { user: data.login.user })
      await onLogin(apolloClient)
    },
    logout: async (context) => {
      await axios.get('/sanctum/csrf-cookie')
      try {
        await apolloClient.mutate({
          mutation: LogoutMutation,
        })
      } finally {
        context.commit(MutationTypes.AUTH_LOGOUT)
        await onLogout(apolloClient)
      }
    },
    forgotPassword: async (_, input: ForgotPasswordInput) => {
      await apolloClient.mutate({
        mutation: ForgotPasswordMutation,
        variables: { input },
      })
    },
    resetPassword: async (_, input: ResetPasswordInput) => {
      await apolloClient.mutate({
        mutation: ResetPasswordMutation,
        variables: { input },
      })
    },
    updatePassword: async (_, input: UpdatePasswordInput) => {
      await apolloClient.mutate({
        mutation: UpdatePasswordMutation,
        variables: { input },
      })
    },
    resendVerification: async () => {
      await apolloClient.mutate({
        mutation: ResendVerificationMutation,
      })
    },
    updateProfile: async ({ commit }, input: UpdateProfileInput) => {
      const { data } = await apolloClient.mutate({
        mutation: UpdateProfileMutation,
        variables: { input },
      })
      commit(MutationTypes.AUTH_UPDATE_PROFILE, { user: data.updateProfile })
    },
  },
}
